import {Button, ControlGroup, FormGroup, InputGroup, Intent, Position} from "@blueprintjs/core";
import * as React from "react";
import {useCallback, useEffect, useRef, useState} from "react";

import {handleChangeString} from "../../../utils/handle-change";
import {PopoverButton} from "../popover-button/popover-button";
import {SubmitGroup} from "../submit-group/submit-group";

interface IPopoverCreateButtonProps {
    placeholder?: string;
    label: string;
    onCreate: (text: string) => Promise<void>;
    closeAfterCreate?: boolean;
    disabled?: boolean;
    position?: Position;
    large?: boolean;
    validate?: (text: string) => string | undefined | Promise<string | undefined>;
}

/**
 * 汎用のポップオーバー内作成フォーム。
 * validate を渡した場合は入力のたびに検証し、エラー表示・Submit抑止を行う。
 */
export const PopoverCreateButton: React.FC<IPopoverCreateButtonProps> = (props) => {
    const {placeholder, label, onCreate, closeAfterCreate, disabled, position, large, validate} =
        props;

    const [isCreating, setIsCreating] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [text, setText] = useState("");
    const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
    const [isValidating, setIsValidating] = useState(false);
    const validationSeqRef = useRef(0);

    /**
     * 非同期バリデーション結果の競合を防ぐため、最後に開始した検証結果のみを反映する。
     */
    const runValidation = useCallback(
        async (nextText: string) => {
            const validationSeq = ++validationSeqRef.current;

            // 未入力時は必須バリデーションをこのコンポーネントで出さない方針。
            // （既存の「空で追加不可」のUXを維持する）
            if (!validate || nextText.length === 0) {
                setIsValidating(false);
                setErrorMessage(undefined);
                return undefined;
            }

            setIsValidating(true);
            const result = await Promise.resolve(validate(nextText));
            // 入力中に次の検証が始まっていた場合は、古い結果をUIに反映しない。
            if (validationSeq !== validationSeqRef.current) {
                return result;
            }
            setErrorMessage(result);
            setIsValidating(false);
            return result;
        },
        [validate],
    );

    useEffect(() => {
        void runValidation(text);
    }, [runValidation, text]);

    const canSubmit = useCallback(
        () => text.length > 0 && !isCreating && !isValidating && !errorMessage,
        [errorMessage, isCreating, isValidating, text.length],
    );

    const submit = useCallback(async () => {
        // Enter / ボタンクリック直前にも再検証して、最新入力値での判定を保証する。
        const submitError = await runValidation(text);
        if (submitError || !canSubmit()) {
            return;
        }

        setIsCreating(true);
        setText("");
        setErrorMessage(undefined);
        try {
            await onCreate(text);
        } finally {
            setIsCreating(false);
        }
        if (closeAfterCreate ?? true) {
            setIsOpen(false);
        }
    }, [canSubmit, closeAfterCreate, onCreate, runValidation, text]);

    const handlePopoverInteraction = useCallback((nextOpenState: boolean) => {
        setIsOpen(nextOpenState);
        if (!nextOpenState) {
            // ポップオーバー再オープン時に前回入力/エラーが残らないよう状態を初期化する。
            setText("");
            setErrorMessage(undefined);
            setIsValidating(false);
        }
    }, []);

    return (
        <PopoverButton
            label={label}
            position={position}
            disabled={disabled}
            large={large}
            isLoading={isCreating}
            isOpen={isOpen}
            onInteraction={(nextOpenState) => handlePopoverInteraction(nextOpenState)}
        >
            <div style={{padding: "10px", width: "320px"}}>
                <SubmitGroup canSubmit={canSubmit()} onSubmit={submit}>
                    <FormGroup
                        intent={errorMessage ? Intent.DANGER : Intent.NONE}
                        helperText={
                            errorMessage ? (
                                // 長いバリデーション文言でもポップオーバー幅を広げずに折り返す。
                                <span style={{whiteSpace: "normal", overflowWrap: "anywhere"}}>
                                    {errorMessage}
                                </span>
                            ) : undefined
                        }
                        style={{marginBottom: 0}}
                    >
                        <ControlGroup fill={true}>
                            <InputGroup
                                placeholder={placeholder}
                                autoFocus={true}
                                value={text}
                                intent={errorMessage ? Intent.DANGER : Intent.NONE}
                                onChange={handleChangeString((s) => setText(s))}
                            />
                            <Button disabled={!canSubmit()} onClick={submit}>
                                追加
                            </Button>
                        </ControlGroup>
                    </FormGroup>
                </SubmitGroup>
            </div>
        </PopoverButton>
    );
};
